import { useForm, type UseFormReturn } from "react-hook-form";
import { useLayoutEffect, useState } from "react";
import { INVALID_PACKAGE_CODE } from "../../utils/constants";
import { makeSalesAwarePackCodeProvider } from "~/SalesCampaign/salesAwarePackCodeProvider";
import { getInitialPriceReference } from "~/AddToCart/redux/addToCart/getInitialPriceReference";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";

/**
 * Trimmed down version of useAddToCartForm
 */

export interface AddToCartHookProps {
  productCode: string;
  initialData?: Partial<AddToCartFormData>;
}

export interface AddToCartFormData {
  productCode: string;
  packCode: string;
  quantity: number;
  priceReference: string;
  salePriceEnabled: boolean;
}

export type PackCodeProvider = (product: ProductDescriptor, quantity: number) => string;

export const useAddToCartForm = ({ productCode, initialData: rawInitialData }: AddToCartHookProps) => {
  const [initialData] = useState<AddToCartFormData>(() => createInitialData(rawInitialData, productCode));
  const formProps = useForm<AddToCartFormData>({ defaultValues: initialData });

  // Register the fields so we can modify them
  useLayoutEffect(() => {
    formProps.register("quantity");
    formProps.register("priceReference");
    formProps.register("packCode");
    formProps.register("salePriceEnabled");
  }, []);

  return formProps;
};

function createInitialData(initialData: Partial<AddToCartFormData> = {}, productCode: string): AddToCartFormData {
  const finalInitialData = {
    productCode,
    priceReference: getInitialPriceReference(),
    packCode: INVALID_PACKAGE_CODE,
    salePriceEnabled: true,
    ...initialData,
    quantity: initialData.quantity ?? 1,
  };
  return finalInitialData;
}

export function usePackagePreselectionEffect(
  editMode: boolean | undefined,
  productDescriptor: ProductDescriptor,
  formProps: UseFormReturn<AddToCartFormData>,
  initialPackCodeProvider?: PackCodeProvider | false,
) {
  useLayoutEffect(() => {
    // if (editMode || formProps.getValues("packCode") !== INVALID_PACKAGE_CODE || !productDescriptor.availability) {
    if (editMode || formProps.getValues("packCode") !== INVALID_PACKAGE_CODE || !productDescriptor.isLoaded) {
      return;
    }
    const packageProvider = initialPackCodeProvider ?? makeSalesAwarePackCodeProvider(null);
    if (packageProvider) {
      const selectedPackCode =
        packageProvider(productDescriptor, formProps.getValues("quantity")) ?? INVALID_PACKAGE_CODE;
      formProps.setValue("packCode", selectedPackCode);
    }
  }, [editMode, productDescriptor.isLoaded]);
}

export function usePackageSizeSyncEffect(
  editMode: boolean | undefined,
  productDescriptor: ProductDescriptor,
  formProps: UseFormReturn<AddToCartFormData>,
) {
  // Until the user changes quantity manually, keep it in sync with package size

  const packCode = formProps.watch("packCode");
  useLayoutEffect(() => {
    const canUpdate = !editMode && !formProps.formState.touchedFields.quantity;
    if (canUpdate) {
      const selectedPack = productDescriptor.availability?.packagingInformation.find(
        (pack) => pack.packCode === packCode,
      );
      if (selectedPack) {
        const packSize = selectedPack.packSize;
        formProps.setValue("quantity", packSize, { shouldDirty: true });
      }
    }
  }, [editMode, packCode]);
}
